import { AfterContentInit, AfterViewInit, ChangeDetectorRef, Component, ContentChildren, ElementRef, forwardRef, Injector, Input, OnDestroy, OnInit, QueryList, TemplateRef, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl, Validators } from '@angular/forms';
import { Dropdown, DropdownChangeEvent } from 'primeng/dropdown';
import { NxTemplateDirective } from '../../directives/nx-template.directive';

export interface EditSelectable {
    name: string;
    id: string;
    disabled?: boolean;
}

@Component({
    selector: 'nx-edit-select-input',
    template: `
    <div class="nx-edit-select">
        <label *ngIf="showLabel" for="select">{{label | translate}}<ng-container *ngIf=isRequired()>*</ng-container></label>
        <p-dropdown
            #dropdown
            [options]="_options"
            [(ngModel)]="selectedId"
            [optionLabel]="'name'"
            [optionValue]="'id'"
            [dataKey]="'id'"
            [appendTo]="'body'"
            [virtualScroll]="false"
            [filter]="filter"
            [disabled]="isDisabled"
            [panelStyleClass]="'nx-edit-select' + (panelWidhtFull ? ' full-width' : '')"
            [autoDisplayFirst]="false"
            [showClear]="showClear"
            [placeholder]="placeholder | translate"
            filterPlaceholder="Suchen..."
            filterBy="name"
            emptyMessage="Keine Einträge gefunden."
            [optionDisabled]="'disabled'"
            [resetFilterOnHide]="true"
            (onChange)="onInputChange($any($event))">
            <ng-template *ngIf="onFilter" pTemplate="filter" let-options="options">
                <input
                    type="text"
                    pInputText
                    #filterElement
                    class="p-dropdown-filter p-inputtext p-component"
                    autocomplete="off"
                    role="textbox"
                    aria-autocomplete="list"
                    aria-controls="dropdown-list"
                    aria-activedescendant="focusedOptionId"
                    (input)="onFilterInputChange($event)"
                />
            </ng-template>
            <ng-template let-value pTemplate="selectedItem" *ngIf="selectedItemTemplate && selected">
                <ng-container *ngTemplateOutlet="selectedItemTemplate; context: { $implicit: value }"></ng-container>
            </ng-template>
            <ng-template let-item pTemplate="item" *ngIf="itemTemplate">
                <ng-container *ngTemplateOutlet="itemTemplate; context: { $implicit: item }"></ng-container>
            </ng-template>
        </p-dropdown>
        <small *ngIf="helpText">{{helpText | translate}}</small>
    </div>
  `,
    styles: [`
  `],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => EditSelectInputComponent),
            multi: true
        }
    ]
})
export class EditSelectInputComponent implements ControlValueAccessor, OnInit, AfterViewInit, AfterContentInit, OnDestroy {

    @ViewChild('dropdown') pDropdown: Dropdown;
    @ViewChild('filterElement') filterElement: ElementRef<HTMLInputElement>;
    private mutationObserver: MutationObserver;
    _options: EditSelectable[] = [];

    ngControl: NgControl;
    isDisabled: boolean;
    @Input() panelWidhtFull = false;
    @Input() label: string;
    @Input() placeholder: string;
    @Input() helpText: string;
    @Input() showClear = false;
    @Input() showLabel = true;

    @Input() set options(options: EditSelectable[]) {
        this._options = options || [];
        if (this.selectedId) {
            this.selected = this._options.find(option => option.id === this.selectedId);
        }
        // IMPORTANT: Update even if selectedId is null/undefined
        // This ensures the dropdown shows the correct empty state
        if (this.pDropdown && this._options.length > 0) {
            setTimeout(() => {
                this.forceDropdownUpdate();
            }, 100);
        }
    }

    @Input() filter = false;
    @Input() onFilter: (value: string) => void;
    @Input() filterHint: string;

    selected: EditSelectable;
    selectedId: string | undefined | null;
    public selectedItemTemplate: TemplateRef<any> | undefined;
    public itemTemplate: TemplateRef<any> | undefined;

    private onChange: (value: string) => void;
    private onTouched: () => void;

    @ContentChildren(NxTemplateDirective) templates!: QueryList<NxTemplateDirective>;

    constructor(
        private _injector: Injector,
        private cdr: ChangeDetectorRef
    ) {}

    ngAfterViewInit(): void {
        console.log('AfterViewInit - selectedId:', this.selectedId, 'options:', this._options);

        // Force update if we have options (even without selectedId)
        if (this.pDropdown && this._options?.length > 0) {
            console.log('ngAfterViewInit triggering forceDropdownUpdate');
            setTimeout(() => {
                this.forceDropdownUpdate();
            }, 100);
        }

        this.observeClassChanges();
    }

    ngAfterContentInit() {
        this.templates.forEach((item) => {
            switch (item.getType()) {
                case 'selectedItem':
                    this.selectedItemTemplate = item.template;
                    break;
                case 'item':
                    this.itemTemplate = item.template;
                    break;
            }
        });
    }

    ngOnInit() {
        this.ngControl = this._injector.get(NgControl);
        this.ngControl.valueAccessor = this;
    }

    ngOnDestroy() {
        this.mutationObserver?.disconnect();
    }

    private forceDropdownUpdate(): void {
        if (!this.pDropdown || !this._options?.length) {
            console.log('Cannot force update - missing requirements', {
                hasDropdown: !!this.pDropdown,
                selectedId: this.selectedId,
                optionsLength: this._options?.length
            });
            return;
        }

        // Force the internal options signal to update
        if (this.pDropdown['_options']) {
            this.pDropdown['_options'].set([...this._options]);
        }

        // Update modelValue signal (even if it's null/undefined)
        if (this.pDropdown['modelValue']) {
            this.pDropdown['modelValue'].set(this.selectedId);
        }

        // Use the public writeValue method
        this.pDropdown.writeValue(this.selectedId, false);

        // Force change detection
        this.pDropdown.cd.detectChanges();
        this.cdr.detectChanges();

    }

    writeValue(id: string | undefined | null): void {
        this.selectedId = id;

        if (this.pDropdown) {
            this.pDropdown.resetFilter();

            // Force update if options are already available
            if (this._options?.length > 0) {
                setTimeout(() => {
                    this.forceDropdownUpdate();
                }, 100);
            }
        }
    }

    registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    onInputChange(event: DropdownChangeEvent): void {
        this.selectedId = event.value;
        if (this.onChange) {
            this.onChange(this.selectedId);
        }
    }

    onBlur(): void {
        if (this.onTouched) {
            this.onTouched();
        }
    }

    isRequired(): boolean {
        return this.ngControl?.control?.hasValidator(Validators.required);
    }

    observeClassChanges() {
        const targetNode = this.pDropdown?.el?.nativeElement;
        if (!targetNode) {
            return;
        }
        this.mutationObserver = new MutationObserver((mutationsList) => {
            for (const mutation of mutationsList) {
                if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                    const classList = (mutation.target as HTMLElement).classList;
                    if (!classList.contains('p-inputwrapper-focus')) {
                        this.onBlur();
                    }
                }
            }
        });
        this.mutationObserver.observe(targetNode, {
            attributes: true,
            attributeFilter: ['class']
        });
    }

    onFilterInputChange(event: Event): void {
        const inputElement = event.target as HTMLInputElement;
        const filterValue = inputElement.value;
        if (this.onFilter) {
            this.onFilter(filterValue);
        }
    }

    /**
     * Opens the dropdown and focuses the filter input
     */
    openAndFocusFilter(): void {
        // Open the dropdown
        this.pDropdown.show();

        // Wait for the dropdown panel to render, then focus the filter input
        setTimeout(() => {
            if (this.filterElement?.nativeElement) {
                this.filterElement.nativeElement.focus();
            }
        }, 10);
    }
}
